import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import Services from './components/Services';
import Pricing from './components/Pricing';
import About from './components/About';
import Footer from './components/Footer';
import Subscription from './components/Subscription';
import ContactFormPopup from '../../components/feature/ContactFormPopup';

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [isFormPopupOpen, setIsFormPopupOpen] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [preselectedPlan, setPreselectedPlan] = useState<'monthly' | 'annual' | undefined>(undefined);

  // ---------------------------------------------------------------------------
  // Scroll detection – updates `scrolled` when the page is scrolled more than 50px
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ---------------------------------------------------------------------------
  // Payment‑success handling – shows a modal when `?payment=success` is present
  // ---------------------------------------------------------------------------
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('payment') === 'success') {
        setShowPaymentSuccess(true);

        // Remove the query string so a refresh does not re‑show the modal
        window.history.replaceState({}, '', window.location.pathname);

        // Auto‑hide after 10 seconds
        const timeoutId = setTimeout(() => setShowPaymentSuccess(false), 10000);
        return () => clearTimeout(timeoutId);
      }
    } catch (err) {
      console.error('Error handling payment success param:', err);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Helper – safely navigate to a new page (fallback to `window.location.href`)
  // ---------------------------------------------------------------------------
  const navigateTo = (path) => {
    try {
      // If the app uses React Router, `window.REACT_APP_NAVIGATE` would be a
      // function injected at runtime. Guard against it being undefined.
      if (typeof window.REACT_APP_NAVIGATE === 'function') {
        window.REACT_APP_NAVIGATE(path);
      } else {
        window.location.href = path;
      }
    } catch (err) {
      console.error(`Navigation to ${path} failed:`, err);
      // Fallback – force navigation
      window.location.href = path;
    }
  };

  const handleStartSubscription = (plan: 'monthly' | 'annual') => {
    setPreselectedPlan(plan);
    setIsFormPopupOpen(true);
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-yellow-50 via-green-50 to-blue-50">
        <Navigation scrolled={scrolled} />

        {/* Payment Success Modal */}
        <AnimatePresence>
          {showPaymentSuccess && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowPaymentSuccess(false)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-5xl text-white"></i>
                  </div>
                  <h2
                    className="text-3xl font-black text-green-600 mb-4"
                    style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}
                  >
                    Payment Successful!
                  </h2>
                  <p className="text-lg text-gray-700 mb-6">
                    Thank you for your purchase! We'll be in touch soon to schedule your
                    service.
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        setShowPaymentSuccess(false);
                        navigateTo('/login');
                      }}
                      className="w-full bg-gradient-to-r from-green-500 to-blue-500 text-white py-4 rounded-full font-bold hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Create Account to Manage Service
                    </button>
                    <button
                      onClick={() => setShowPaymentSuccess(false)}
                      className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <Hero onGetStartedClick={() => setIsFormPopupOpen(true)} />
        <Services />
        <Pricing />
        <About />
        <Subscription onStartSubscription={handleStartSubscription} />
        <Footer />
      </div>
      <ContactFormPopup
        isOpen={isFormPopupOpen}
        onClose={() => {
          setIsFormPopupOpen(false);
          setPreselectedPlan(undefined);
        }}
        preselectedPlan={preselectedPlan}
      />
    </>
  );
}
